import { TextArea as UiTextArea } from "../ui/TextArea";
import { Pencil, X } from "lucide-react";
import { useState } from "react";
import { centsToInput, formatMoney, parseMoneyToCents } from "../../lib/format";
import type { CashClosedPayload, CashSession, CashSummary } from "../../types";
import { nowIso } from "../../utils/dates";
import { AppModal, Button, Metric } from "../ui";
import { NumericKeypadModal } from "../ui/NumericKeypadModal";
import { getExpectedCashAfterStackerCollections } from "../../features/cash-registers/services/cashSummary";

type CloseCashModalProps = {
  openOrderCount: number;
  cashSession: CashSession;
  cashlogyCashCents: number | null;
  cashlogyStackerCollectionsCents: number;
  isBusy: boolean;
  onCancel: () => void;
  onConfirm: (payload: CashClosedPayload) => void;
  summary: CashSummary;
  userId: string;
};

export function CloseCashModal({
  openOrderCount,
  cashSession,
  cashlogyCashCents,
  cashlogyStackerCollectionsCents,
  isBusy,
  onCancel,
  onConfirm,
  summary,
  userId,
}: CloseCashModalProps) {
  const expectedCashCents = getExpectedCashAfterStackerCollections(
    summary.cashCents,
    cashlogyStackerCollectionsCents,
  );
  const [countedCash, setCountedCash] = useState(
    centsToInput(cashlogyCashCents ?? expectedCashCents),
  );
  const [countedCard, setCountedCard] = useState(
    centsToInput(summary.cardCents),
  );
  const [countedAmountKeypad, setCountedAmountKeypad] = useState<
    "cash" | "card" | null
  >(null);
  const [notes, setNotes] = useState("");
  const countedCashCents = parseMoneyToCents(countedCash);
  const countedCardCents = parseMoneyToCents(countedCard);
  const finalCashFundCents = cashlogyCashCents ?? cashSession.openingFloatCents;
  const expectedTotal = expectedCashCents + summary.cardCents;
  const countedTotal = countedCashCents + countedCardCents;
  const discrepancy = countedTotal - expectedTotal;
  const notesRequired = discrepancy !== 0 && !notes.trim();

  function handleConfirm() {
    onConfirm({
      sessionId: cashSession.id,
      tenantId: cashSession.tenantId,
      closedAt: nowIso(),
      closedBy: userId,
      expectedCashCents,
      expectedCardCents: summary.cardCents,
      expectedInvitationCents: summary.invitationCents,
      expectedOtherCents: summary.otherCents,
      countedCashCents,
      countedCardCents,
      countedInvitationCents: summary.invitationCents,
      countedOtherCents: summary.otherCents,
      discrepancyCents: discrepancy,
      finalCashFundCents,
      notes: notes.trim(),
    });
  }

  function handleCountedAmountConfirm(value: string) {
    const normalizedValue = centsToInput(parseMoneyToCents(value));

    if (countedAmountKeypad === "cash") {
      setCountedCash(normalizedValue);
    } else if (countedAmountKeypad === "card") {
      setCountedCard(normalizedValue);
    }

    setCountedAmountKeypad(null);
  }

  return (
    <>
      <AppModal
        dismissDisabled={isBusy || countedAmountKeypad !== null}
        label="Cierre de caja"
        onClose={onCancel}
        maxWidth={600}
      >
        <section className="max-h-[calc(100svh-32px)] w-full overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--surface)] p-5 text-[var(--foreground)] shadow-[var(--shadow)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">Cierre de caja</h2>
              
            </div>
            <Button className="!size-11 !min-h-11 !min-w-11 !shrink-0 !rounded-[12px] !p-0" aria-label="Cerrar"
              disabled={isBusy}
              onClick={onCancel}
              size="sm"
              type="button"
              variant="tertiary"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Metric
              label="Efectivo esperado"
              value={formatMoney(expectedCashCents)}
            />
            <Metric
              label="Tarjeta TPV"
              value={formatMoney(summary.cardCents)}
            />
          </div>

          {cashlogyCashCents !== null ? (
            <p className="mt-4 rounded-[var(--radius)] border border-emerald-500/35 bg-emerald-500/10 p-3 text-sm font-semibold text-emerald-800 dark:text-emerald-200">
              El efectivo contado y el fondo final se han consultado
              automáticamente en Cashlogy. El importe permanece oculto para
              proteger el contenido del stacker.
            </p>
          ) : null}

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              {
                field: "cash" as const,
                label: "Efectivo",
                value: countedCash,
              },
              {
                field: "card" as const,
                label: "Datafono",
                value: countedCard,
              },
            ].map(({ field, label, value }) => (
              <div className="block" key={field}>
                <span className="text-sm font-semibold text-[var(--muted)]">
                  {label}
                </span>
                <button
                  aria-label={
                    field === "cash" && cashlogyCashCents !== null
                      ? "Efectivo consultado en Cashlogy"
                      : `Introducir ${label.toLowerCase()}`
                  }
                  className="mt-1 flex h-12 w-full items-center rounded-[var(--radius)] border border-[var(--field-border)] bg-[var(--field)] text-left text-[var(--field-foreground)] outline-none focus:ring-2 focus:ring-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={
                    isBusy ||
                    (field === "cash" && cashlogyCashCents !== null)
                  }
                  onClick={() => setCountedAmountKeypad(field)}
                  type="button"
                >
                  <span className="px-3 font-mono text-sm font-bold text-[var(--muted)]">
                    EUR
                  </span>
                  <span className="min-w-0 flex-1 px-2 font-mono">
                    {field === "cash" && cashlogyCashCents !== null
                      ? "••••"
                      : value}
                  </span>
                  {field !== "cash" || cashlogyCashCents === null ? (
                    <Pencil aria-hidden="true" className="mr-3 h-4 w-4 shrink-0 text-[var(--muted)]" />
                  ) : null}
                </button>
              </div>
            ))}
          </div>

          <label className="mt-5 block">
            <span className="text-sm font-semibold text-[var(--muted)]">
              Notas
            </span>
            <UiTextArea
              className="mt-1 min-h-14 w-full rounded-[var(--radius)] border border-[var(--field-border)] bg-[var(--field)] p-3 text-[var(--field-foreground)] outline-none focus:ring-2 focus:ring-[var(--accent)]"
              onChange={(event) => setNotes(event.target.value)}
              value={notes}
            />
            {notesRequired ? (
              <span className="mt-2 block text-sm font-semibold text-[var(--danger)]">
                Indica el motivo del descuadre para poder cerrar la caja.
              </span>
            ) : null}
          </label>

          {openOrderCount > 0 ? <div className="mt-4 rounded-[var(--radius)] border border-[var(--separator)] p-3">
            <p className="text-sm">Hay {openOrderCount} pedidos abiertos en el local. Seguirán disponibles en las demás cajas y en la próxima apertura. El importe pendiente de cobro no se incluirá en este cierre.</p>
            <Button className="mt-3" disabled={isBusy} onClick={onCancel} type="button" variant="tertiary">Volver y revisar</Button>
          </div> : null}
          <Button
            className="mt-4"
            disabled={isBusy || notesRequired || finalCashFundCents < 0}
            fullWidth
            onClick={handleConfirm}
            size="lg"
            type="button"
            variant="danger"
          >
            Cerrar caja
          </Button>
        </section>
      </AppModal>

      {countedAmountKeypad ? (
        <NumericKeypadModal
          confirmLabel="Aceptar"
          disabled={isBusy}
          initialValue={
            countedAmountKeypad === "cash" ? countedCash : countedCard
          }
          maxFractionDigits={2}
          onCancel={() => setCountedAmountKeypad(null)}
          onConfirm={handleCountedAmountConfirm}
          showCloseButton={false}
          title={countedAmountKeypad === "cash" ? "Efectivo" : "Datáfono"}
          unit="EUR"
        />
      ) : null}
    </>
  );
}
